import { useEffect, useState } from 'react';
import { fetchAllBusinesses } from '@/utils/homepageApi';
import type { Business } from '@/types/homepage';

/**
 * Live operators on Wildy, fetched once per visit and shared by every page that shows them
 * (What we offer counts, the For business "Already on the map" strip), so the numbers always agree.
 * Live data is an extra: callers must render correctly while loading and when it fails.
 */

export type LiveBusiness = Business;

export type LiveBusinesses =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ready'; list: LiveBusiness[] };

const TIMEOUT_MS = 12000;
let shared: Promise<LiveBusiness[]> | null = null;

function load(): Promise<LiveBusiness[]> {
  if (!shared) {
    const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS));
    shared = Promise.race([fetchAllBusinesses() as Promise<LiveBusiness[]>, timeout])
      .then(list => (Array.isArray(list) ? list : [])
        .filter(b => b && b._id && b.name && (!b.status || b.status === 'active')))
      .catch(err => { shared = null; throw err; }); // let a later visit retry
  }
  return shared;
}

export function useLiveBusinesses(): LiveBusinesses {
  const [value, setValue] = useState<LiveBusinesses>({ state: 'loading' });
  useEffect(() => {
    let alive = true;
    load()
      .then(list => alive && setValue(list.length ? { state: 'ready', list } : { state: 'error' }))
      .catch(() => alive && setValue({ state: 'error' }));
    return () => { alive = false; };
  }, []);
  return value;
}

/** Distinct countries among live operators. */
export const countCountries = (list: LiveBusiness[]) =>
  new Set(list.map(b => b.location?.address?.country).filter(Boolean)).size;
